import { Divider, Popover, Tooltip, Typography } from 'antd';
import React, { FC } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { ReactComponent as RightArrowSVG } from 'assets/images/right_arrow.svg';
import { CaseTypeEnum, USER_ROLES } from '../../../config/constants';
import './dashboard-layout.styles.less';
import styled from 'styled-components';
import smallLogoSvg from 'assets/images/auth/small-logo.svg';
import logoSvg from 'assets/images/dashboard/logo.svg';
import { ReactComponent as MainSVG } from 'assets/images/dashboard/main.svg';
import { ReactComponent as PatientsSVG } from 'assets/images/dashboard/patients.svg';
import { ReactComponent as EducationSVG } from 'assets/images/dashboard/education.svg';
import { ReactComponent as JourneySVG } from 'assets/images/dashboard/journey.svg';
import { ReactComponent as CartSVG } from 'assets/images/dashboard/cart.svg';
import { IUser } from '../../../modules/auth/services/type';

export const MENU_DURATION = '0.2s';

const ItemStyled = styled.div<{ active: boolean; smallMode: boolean; disabled: boolean }>`
  position: relative;
  padding-left: 30px;
  padding-top: 10px;
  padding-bottom: 10px;
  color: 'white';
  cursor: ${({ disabled }) => (disabled ? 'auto' : 'pointer')};

  :hover {
    background: ${({ disabled }) => (disabled ? 'transparent' : '#1890ff5e')};
  }

  .menuBack {
    transition: width ${MENU_DURATION};
    width: ${({ active, smallMode }) => (active && !smallMode ? '140px' : '40px')};
    height: 40px;
    box-shadow: inset 0px 0.5px 4px rgb(96 97 112 / 32%);
    border-radius: 20px;
    position: absolute;
    color: ${({ disabled }) => (disabled ? 'black' : 'white')};
    background: ${({ disabled }) => (disabled ? 'rgba(225, 225, 225, 0.75)' : '#f9f9fa')};
  }

  .menuContext {
    margin-left: 11px;
    padding-top: 11px;
    z-index: 1;
    position: absolute;

    svg {
      fill: ${({ active }) => (active ? 'white' : '#050D43')};
    }
  }
`;

const NotificationDot = styled.div<{ active: boolean }>`
  position: absolute;
  top: 3px;
  left: 20px;

  width: 10px;
  height: 10px;
  border-radius: 5px;

  background-color: ${(props) => (props.active ? 'white' : 'red')};
`;

interface IMenuItemProps {
  active: boolean;
  onClick?: () => void;
  icon: React.ReactNode;
  text: string;
  smallMode: boolean;
  notification?: boolean;
  disabled?: boolean;
  tooltip?: string;
}

const MenuItem: FC<IMenuItemProps> = ({
  active,
  onClick,
  icon,
  text,
  smallMode,
  notification = false,
  disabled = false,
  tooltip,
}) => (
  <ItemStyled active={active} smallMode={smallMode} onClick={disabled ? () => {} : onClick} disabled={disabled}>
    <Tooltip placement="right" title={disabled ? tooltip : null}>
      <div style={{ height: '40px' }}>
        <div className="flex align-items-center menuContext ">
          {icon}
          {!smallMode && <span className="ml-4">{text}</span>}
          {notification && <NotificationDot active={active} />}
        </div>
        <div
          className="menuBack"
          style={{ backgroundImage: active ? 'linear-gradient(27.16deg, #DA3A6D 21.81%, #DA533C 65.38%)' : 'unset' }}
        />
      </div>
    </Tooltip>
  </ItemStyled>
);

interface IDashboardMenuProps {
  user: IUser;
  activeMenu: string;
  showOnboardingWizard: boolean;
  onSetShowOnboardingWizard: (show: boolean) => void;
  isOnboardingContentVisible: boolean;
  onSetIsOnboardingContentVisible: (show: boolean) => void;
  hilightWizardPopover?: boolean;
  smallMode?: boolean;
}

export const DashboardMenu: FC<IDashboardMenuProps> = ({
  user,
  activeMenu,
  showOnboardingWizard,
  onSetShowOnboardingWizard,
  isOnboardingContentVisible,
  onSetIsOnboardingContentVisible,
  hilightWizardPopover = false,
  smallMode = false,
}) => {
  const history = useHistory();

  const onVisibleChange = (visible: boolean) => {
    if (showOnboardingWizard) {
      onSetIsOnboardingContentVisible(true);
    } else {
      setTimeout(() => {
        onSetIsOnboardingContentVisible(visible);
      }, 400);
    }
  };

  const navigateToOnboarding = (text: string) => {
    switch (text) {
      case 'duo-case':
        onSetIsOnboardingContentVisible(false);
        onSetShowOnboardingWizard(false);
        history.push(`/${user?.type}/patients/submit/${CaseTypeEnum.DUO}?open=onboarding`);
        break;
      case 'proposal':
        onSetIsOnboardingContentVisible(false);
        onSetShowOnboardingWizard(false);
        history.push(`/${user?.type}/patients/987654321/design/987654321?open=onboarding`);
        break;
      default:
    }
  };

  // TODO: reference to - https://frontierco.atlassian.net/browse/FRON-1762
  // const location = useLocation();
  // const onOrientationVideoClick = () => {
  //   onSetIsOnboardingContentVisible(false);
  //   history.push(`${location.pathname}?open=onboardingVideo`);
  // };

  const onBoardingContent = (
    <div className="w-15rem p-2 start-onboarding">
      <Typography.Text className="font-bold text-sm">Quick Guides</Typography.Text>
      <Divider className="mt-1 mb-2" />
      <div className="mr-1">
        <Typography.Text className="text-xs line-height-1" style={{ color: '#6b7d8b' }}>
          Select where you would like to start
        </Typography.Text>

        {/* TODO: reference to - https://frontierco.atlassian.net/browse/FRON-1762 */}
        {/* <div
          className="flex align-items-center justify-content-between mt-3 cursor-pointer"
          onClick={onOrientationVideoClick}
        >
          <Typography.Text className="text-xs line-height-1">
            Orientation video
          </Typography.Text>
          <RightArrowSVG />
        </div> */}

        <div
          className="flex align-items-center justify-content-between mt-3 cursor-pointer"
          onClick={() => navigateToOnboarding('duo-case')}
        >
          <Typography.Text className="text-xs line-height-1">Submitting a Duo case</Typography.Text>
          <RightArrowSVG />
        </div>

        <div
          className="flex align-items-center justify-content-between mt-3 cursor-pointer"
          onClick={() => navigateToOnboarding('proposal')}
        >
          <Typography.Text className="text-xs line-height-1">Receiving my proposal</Typography.Text>
          <RightArrowSVG />
        </div>
      </div>
    </div>
  );

  return (
    <div
      style={{
        color: 'white',
        fontWeight: 400,
        fontSize: '12px',
      }}
    >
      <div className="py-5 flex justify-content-center">
        <Link to="/">
          {smallMode ? <img src={smallLogoSvg} width="36px" alt="logo" /> : <img src={logoSvg} alt="logo" />}
        </Link>
      </div>

      <div>
        <MenuItem
          smallMode={smallMode}
          active={activeMenu === (user?.type || 'dentist')}
          onClick={() => history.push(`/${user?.type || 'dentist'}`)}
          icon={<MainSVG fill="white" />}
          text="Dashboard"
        />

        {user?.type !== USER_ROLES.MANUFACTURER && (
          <MenuItem
            smallMode={smallMode}
            active={activeMenu === 'patients'}
            onClick={() => history.push(`/${user?.type}/patients`)}
            icon={<PatientsSVG />}
            text="Patients"
          />
        )}

        {user?.type === USER_ROLES.DENTIST && (
          <MenuItem
            smallMode={smallMode}
            active={activeMenu === 'education'}
            onClick={() => history.push(`/${user?.type}/education`)}
            icon={<EducationSVG />}
            text="Education"
            notification={user.newContentAssigned}
            // disabled={user?.status === 'applied'}
          />
        )}

        {user?.type === USER_ROLES.DENTIST && (
          <>
            {isOnboardingContentVisible && hilightWizardPopover && <div className="journeyPopoverBg" />}
            <Popover
              content={onBoardingContent}
              visible={isOnboardingContentVisible}
              onVisibleChange={onVisibleChange}
              title=""
              placement="right"
              trigger="click"
            >
              <MenuItem
                smallMode={smallMode}
                active={activeMenu === 'onboarding-wizard'}
                icon={<JourneySVG />}
                text="Guides"
                disabled={user?.status === 'applied'}
                tooltip="Complete your onboarding steps to unlock Guide"
              />
            </Popover>
          </>
        )}
        {user?.type === USER_ROLES.DENTIST && (
          <MenuItem
            smallMode={smallMode}
            active={activeMenu === 'pricing'}
            onClick={() => history.push(`/${user?.type}/pricing`)}
            icon={<CartSVG fill="white" />}
            text="Pricing"
            disabled={user?.status === 'applied'}
            tooltip="Complete your onboarding steps to unlock Pricing"
          />
        )}
      </div>
    </div>
  );
};
